import { AppButton } from '@clickguard/design-system'
import RefreshCw01 from '@untitled-ui/icons-react/build/esm/RefreshCw01'
import ArrowCircleBrokenUp from '@untitled-ui/icons-react/build/esm/ArrowCircleBrokenUp'
import CursorClick01 from '@untitled-ui/icons-react/build/esm/CursorClick01'
import ArrowCircleRight from '@untitled-ui/icons-react/build/esm/ArrowCircleRight'
import moment from 'moment'
import { ComponentType, SVGProps } from 'react'

const COLUMNS = [
	'Account',
	'Submission date',
	'Date range',
	'Submission status',
	'Invalid Clicks Reported'
]

/** Figma metric-tile shadow (Shadows/xs-ish), node 6472:205909. */
const TILE_SHADOW =
	'shadow-[0px_4px_16px_-2px_rgba(16,24,40,0.03),0px_1px_4px_-2px_rgba(16,24,40,0.02)]'

/**
 * KPI tile matching the Figma "NewMetricTile" (node 6672:161936): 16px padding,
 * fixed 108px height, 12px SemiBold label with a leading 12px icon, and an 18px
 * SemiBold value. Not the DS MetricCard (which is 14/20px, borderless, px-4 py-3).
 */
const MetricTile = ({
	icon: Icon,
	label,
	value
}: {
	icon: ComponentType<SVGProps<SVGSVGElement>>
	label: string
	value: string
}) => (
	<div
		className={`h-[108px] w-full overflow-hidden rounded-[12px] border border-t-checkbox bg-white p-4 ${TILE_SHADOW}`}
	>
		<div className="flex items-center gap-1.5 h-4">
			<Icon className="w-3 h-3 text-t-coupon" />
			<span className="text-[12px] leading-4 font-semibold text-t-coupon">
				{label}
			</span>
		</div>
		<p className="mt-1 text-[18px] leading-[28px] font-semibold text-t-coupon">
			{value}
		</p>
	</div>
)

interface EmptyPostSetupProps {
	/** From the reshaped summary — `preferences.auto_refunds_next_submission_at`. */
	nextSubmissionAt: string | null
	onRefresh: () => void
	refreshing?: boolean
}

/**
 * §4 — Post-setup empty state: agent launched but no terminal submissions yet.
 * Figma 6472-205852. Spec B4.
 */
export const EmptyPostSetup = ({
	nextSubmissionAt,
	onRefresh,
	refreshing
}: EmptyPostSetupProps) => {
	const nextLabel = nextSubmissionAt
		? moment(nextSubmissionAt).format('MMM D, YYYY')
		: '–'

	return (
		<div className="space-y-6">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
				<MetricTile
					icon={ArrowCircleBrokenUp}
					label="Total submissions"
					value="–"
				/>
				<MetricTile
					icon={CursorClick01}
					label="Total invalid clicks reported"
					value="–"
				/>
				<MetricTile
					icon={ArrowCircleRight}
					label="Next submission"
					value={nextLabel}
				/>
			</div>

			{/* Submission history — Figma node 6669:161509: an outer #f6f6f6 card
			    (title bar) wrapping an inner white bordered card (refresh + table). */}
			<div
				className={`flex flex-col overflow-hidden rounded-[12px] border border-t-checkbox bg-table-band ${TILE_SHADOW}`}
			>
				<div className="px-4 py-3">
					<h3 className="text-[16px] leading-[22px] font-medium text-t-coupon">
						Submission history
					</h3>
				</div>

				<div
					className={`flex flex-col gap-3 overflow-hidden rounded-[12px] border border-t-checkbox bg-white p-3 ${TILE_SHADOW}`}
				>
					<div className="flex items-center">
						<div className="flex-1" />
						<AppButton
							loader="dots"
							variant="secondary"
							roundness="default"
							size="md"
							leftIcon={<RefreshCw01 className="h-4 w-4" />}
							label="Refresh"
							isLoading={refreshing}
							onClick={onRefresh}
						/>
					</div>

					<div className="overflow-x-auto rounded-[8px]">
						<table className="w-full table-fixed text-left">
							<thead>
								<tr className="bg-table-band">
									{COLUMNS.map((c) => (
										<th
											key={c}
											className="h-11 px-6 text-[12px] leading-4 font-semibold text-t-desc-gray whitespace-nowrap border-b border-t-checkbox"
										>
											{c}
										</th>
									))}
									<th
										aria-hidden
										className="h-11 w-[131px] border-b border-t-checkbox"
									/>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td
										colSpan={COLUMNS.length + 1}
										className="h-[72px] px-6 text-center text-[14px] leading-[18px] text-t-coupon"
									>
										No refund requests submitted yet
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	)
}
